import { Link } from 'react-router-dom'
import {
  ArrowRight,
  Bot,
  CalendarCheck,
  CheckCircle,
  ClipboardCheck,
  FileStack,
  HardHat,
  LineChart,
} from 'lucide-react'
import { APP_REGISTER_URL, PRODUCT_LINKS } from '../constants/nav'
import FadeIn from '../components/animations/FadeIn'
import StaggerContainer, { StaggerItem } from '../components/animations/StaggerContainer'

const iconMap = {
  Projects: CalendarCheck,
  Benchmark: ClipboardCheck,
  Design: FileStack,
  Pro: HardHat,
}

const productPages = {
  Projects: {
    eyebrow: 'Project controls',
    headline: 'SiteDeck Projects keeps schedule, cost, procurement, and risk in one PM view.',
    intro:
      'Project managers need more than task lists. SiteDeck Projects is the control layer that ties field progress, owner commitments, deliveries, cost pressure, and upcoming risks back to the same project record.',
    promise: 'SiteDeck sees the project before drift becomes damage.',
    points: [
      'Schedule look-ahead tied to actual field status',
      'Procurement and material risk visible before crews are waiting',
      'Cost and change signals connected to daily execution',
      'AI risk briefs that tell the PM what needs attention first',
    ],
    workflow: [
      'Pulls status from Pro field activity',
      'Connects open issues to schedule and cost exposure',
      'Surfaces owner, vendor, and internal commitments',
      'Feeds AI summaries for morning and weekly project review',
    ],
    cta: 'Book a Projects Demo',
    asideTitle: 'PM command layer',
    asideCopy:
      'Built for the project lead who needs to know where the job is drifting before the schedule update catches up.',
  },
  Benchmark: {
    eyebrow: 'Quality management',
    headline: 'SiteDeck Benchmark turns inspections, NCRs, and hold points into a living quality record.',
    intro:
      'Quality issues get expensive when the paperwork is separated from the work. Benchmark keeps inspection plans, corrective actions, photos, signoffs, and audit history tied to the project timeline.',
    promise: 'Quality control that stays connected to the work.',
    points: [
      'Inspection records aligned to project areas and activities',
      'NCRs tracked from discovery through closeout',
      'Photo and signoff history that is easy to defend later',
      'Quality risk surfaced alongside schedule and field status',
    ],
    workflow: [
      'Defines the quality checkpoints that matter',
      'Captures inspection evidence in the field',
      'Escalates exceptions before they become rework',
      'Preserves the audit trail for owner and internal review',
    ],
    cta: 'Talk Quality Workflow',
    asideTitle: 'Benchmark lane',
    asideCopy:
      'The quality system is designed to become more valuable as it links with Pro, Projects, and Design.',
  },
  Design: {
    eyebrow: 'Drawing repository',
    headline: 'SiteDeck Design keeps current drawings, revisions, and workflows from getting lost.',
    intro:
      'The wrong drawing can create the most expensive mistake on a job. SiteDeck Design is the planned repository for current revisions, review workflows, and document context that follows the work.',
    promise: 'Current drawings without the revision hunt.',
    points: [
      'Current revision visibility for field and project teams',
      'Drawing workflow tied to open work and quality checks',
      'Revision context preserved when issues are reviewed later',
      'AI assistance for finding conflicts and missed follow-ups',
    ],
    workflow: [
      'Stores the current drawing truth',
      'Links revisions to project issues and quality records',
      'Flags outdated context before it reaches the field',
      'Supports future document workflows and approvals',
    ],
    cta: 'Discuss Drawing Workflow',
    asideTitle: 'Design lane',
    asideCopy:
      'This lane keeps the homepage honest: Design is part of the platform vision without pretending every workflow is finished today.',
  },
  Pro: {
    eyebrow: 'Field operations',
    headline: 'SiteDeck Pro gives crews and supervisors one place for jobsite execution.',
    intro:
      'Pro is the live field tool for teams, safety, lessons learned, daily activity, and supervisor visibility. It replaces paper forms, spreadsheets, and disconnected text threads with a shared field record.',
    promise: 'Every job site. One deck.',
    points: [
      'Crew management and jobsite communication',
      'Lessons learned, safety workflows, and field documentation',
      'Supervisor visibility without pulling crews into office software',
      'A field record that feeds project controls and AI insight',
    ],
    workflow: [
      'Captures what happened in the field',
      'Organizes updates by project, crew, and work area',
      'Feeds Projects, Benchmark, and reporting workflows',
      'Gives the AI agent the ground truth it needs',
    ],
    cta: 'Try SiteDeck Pro Free',
    asideTitle: 'Live product',
    asideCopy:
      'SiteDeck Pro is the current field-facing product and the first lane contractors can start using right away.',
  },
}

type ProductStubProps = {
  product: (typeof PRODUCT_LINKS)[number]
}

export default function ProductStub({ product }: ProductStubProps) {
  const Icon = iconMap[product.label as keyof typeof iconMap]
  const content = productPages[product.label as keyof typeof productPages]

  return (
    <>
      <section className="bg-navy py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeIn>
            <div className="grid lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
              <div>
                <div className="inline-flex items-center gap-2 text-field-orange text-sm font-semibold mb-5">
                  <Icon className="w-4 h-4" />
                  {content.eyebrow}
                </div>
                <h1 className="text-white text-4xl sm:text-[52px] font-heading font-semibold leading-tight mb-5">
                  {content.headline}
                </h1>
                <p className="text-white/70 text-lg leading-relaxed max-w-3xl mb-8">
                  {content.intro}
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <Link to="/demo" className="btn-primary">
                    {product.label === 'Pro' ? 'Book a Demo' : content.cta}
                  </Link>
                  {product.label === 'Pro' && (
                    <a href={APP_REGISTER_URL} className="btn-ghost">
                      {content.cta}
                    </a>
                  )}
                </div>
              </div>

              <aside className="bg-white/8 border border-white/10 rounded-xl p-5">
                <p className="section-label mb-3">Status</p>
                <p className="text-white font-heading text-2xl font-semibold mb-2">
                  {product.status}
                </p>
                <p className="text-white/60 text-sm leading-relaxed mb-5">
                  {content.asideCopy}
                </p>
                <div className="bg-navy-mid rounded-lg p-4">
                  <p className="text-field-orange text-sm font-semibold mb-2">
                    {content.asideTitle}
                  </p>
                  <p className="text-white text-lg font-heading font-semibold leading-snug">
                    {content.promise}
                  </p>
                </div>
              </aside>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="bg-off-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 lg:gap-14 items-start">
            <FadeIn>
              <p className="section-label mb-3">Why it matters</p>
              <h2 className="text-navy text-3xl sm:text-[42px] font-heading font-semibold leading-tight mb-5">
                {content.promise}
              </h2>
              <p className="text-slate text-lg leading-relaxed">
                Each SiteDeck lane is useful on its own, but the real value comes from keeping
                field execution, project controls, quality, drawings, and AI insight connected.
              </p>
            </FadeIn>

            <StaggerContainer className="grid sm:grid-cols-2 gap-4">
              {content.points.map((point) => (
                <StaggerItem key={point}>
                  <div className="bg-white border border-gray-100 rounded-lg p-5 h-full">
                    <CheckCircle className="w-5 h-5 text-site-green mb-3" />
                    <p className="text-navy font-semibold leading-snug">{point}</p>
                  </div>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-start">
            <FadeIn>
              <p className="section-label mb-3">Connected workflow</p>
              <h2 className="text-navy text-3xl sm:text-[42px] font-heading font-semibold leading-tight mb-5">
                Built as one project system, not five disconnected websites.
              </h2>
              <p className="text-slate text-lg leading-relaxed mb-8">
                SiteDeck can introduce each lane clearly without forcing visitors to learn the
                whole platform at once. The page gives them the lane they clicked and shows how it
                plugs back into the project.
              </p>
              <Link
                to="/"
                className="inline-flex items-center text-field-orange text-sm font-semibold hover:underline"
              >
                Back to platform overview
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </FadeIn>

            <div className="bg-off-white border border-gray-100 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-5">
                <Bot className="w-5 h-5 text-field-orange" />
                <p className="text-navy font-heading font-semibold">How this lane connects</p>
              </div>
              <div className="space-y-3">
                {content.workflow.map((step, index) => (
                  <div key={step} className="bg-white border border-gray-100 rounded-lg p-4 flex gap-3">
                    <div className="w-7 h-7 rounded-full bg-field-orange text-white text-sm font-semibold flex items-center justify-center shrink-0">
                      {index + 1}
                    </div>
                    <p className="text-slate text-sm leading-relaxed">{step}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 border-t border-gray-200 pt-5 flex items-start gap-3">
                <LineChart className="w-5 h-5 text-field-orange shrink-0 mt-0.5" />
                <p className="text-slate text-sm leading-relaxed">
                  The AI agent becomes more useful as each lane contributes cleaner project data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
